import type { TourFormStepId } from '@/features/tour/tour-form-model'
import type { TourFormStepItem } from './use-tour-form-steps'
import { Check, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TOUR_FORM_STEP_LABELS } from '@/shared/constants/tour-form'
import { cn } from '@/shared/libs/cn'

interface StepNavProps {
  steps: TourFormStepItem[]
  onSelect: (id: TourFormStepId) => void
}

/** Step list of the tour form: vertical rail on wide screens, a horizontal strip below `lg`. */
export function StepNav({ steps, onSelect }: StepNavProps) {
  const { t } = useTranslation()

  return (
    <nav
      aria-label={t('tour.steps', { defaultValue: 'Các bước' })}
      className="lg:sticky lg:top-6 lg:self-start"
    >
      <ol className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
        {steps.map((step, index) => {
          const label = TOUR_FORM_STEP_LABELS[step.id]
          return (
            <li key={step.id} className="shrink-0 lg:shrink">
              <button
                type="button"
                disabled={!step.isUnlocked}
                aria-current={step.isActive ? 'step' : undefined}
                onClick={() => onSelect(step.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  step.isActive
                    ? 'bg-zinc-100 font-semibold text-zinc-900'
                    : 'font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900',
                  !step.isUnlocked && 'cursor-not-allowed text-zinc-400 hover:bg-transparent',
                )}
              >
                <span
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                    step.isActive && 'border-primary bg-primary text-primary-foreground',
                    !step.isActive && step.isComplete && 'border-primary text-primary',
                    !step.isActive && !step.isComplete && 'border-zinc-300 text-zinc-500',
                  )}
                >
                  {step.isComplete && !step.isActive ? (
                    <Check size={14} aria-hidden />
                  ) : !step.isUnlocked ? (
                    <Lock size={12} aria-hidden />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="whitespace-nowrap">
                  {t(label.key, { defaultValue: label.defaultValue })}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
